import React from "react";
import { badge, button } from "components/designTokens";
import c from "classnames";
import { Pen, XCircle, Rubbish, VideoCamera } from "components/icons";
import { formatDate } from "date";

export function List({
  program,
  user,
  onCreateEpisode,
  onUpdateEpisode,
  episodeList,
  onDeleteEpisode,
}) {
  const [showCreateButton, setShowCreateButton] = React.useState(true);
  const [showCreationForm, setShowCreationForm] = React.useState(false);

  const closeCreationForm = () => {
    setShowCreationForm(false);
    setShowCreateButton(true);
  };

  const openCreationForm = () => {
    setShowCreationForm(true);
    setShowCreateButton(false);
  };

  const handleSubmit = async (values) => {
    await onCreateEpisode(values);
    closeCreationForm();
  };

  return (
    <div className="mt-8">
      {program && user ? (
        <Program program={program} user={user}></Program>
      ) : null}
      <Header
        showCreateButton={showCreateButton}
        onCreateClick={openCreationForm}
      ></Header>
      {showCreationForm ? (
        <Form onCancleClick={closeCreationForm} onSubmit={handleSubmit}></Form>
      ) : null}
      {episodeList.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md mt-8">
          <ul>
            {episodeList.map((item, index) => {
              return (
                <EpisodeItem
                  key={item.id}
                  episode={item}
                  bordered={index > 0}
                  onDelete={onDeleteEpisode}
                  onUpdate={onUpdateEpisode}
                ></EpisodeItem>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function Header({ onCreateClick, showCreateButton }) {
  return (
    <div className="mt-8 pb-5 border-gray-200 space-y-3 sm:flex sm:items-center sm:justify-between sm:space-x-4 sm:space-y-0">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Proram Episode
      </h3>
      <div>
        {showCreateButton ? (
          <span className="shadow-sm rounded-md">
            <button
              className={`inline-flex items-center px-4 py-2 text-sm leading-5 font-medium ${button.primary}`}
              onClick={onCreateClick}
            >
              Create new episode
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function Program({ program, user }) {
  return (
    <div className="flex items-center">
      <div className="flex-shrink-0 w-24">
        <img
          className="w-full rounded"
          src={program.thumbnail}
          alt={program.title}
        />
      </div>
      <div className="ml-4">
        <div className="text-sm leading-5 font-medium text-gray-900">
          {program.title}
        </div>
        <div className="text-sm leading-5 text-gray-500">
          <span className="inline-block mr-4">{`#${program.id}`}</span>
          <span className="inline-block">{user.display_name}</span>
        </div>
      </div>
    </div>
  );
}

export function EpisodeItem({ episode, bordered, onDelete, onUpdate }) {
  const [state, dispatch] = React.useReducer(
    (state, action) => {
      switch (action.type) {
        case "OPEN_EDIT_FORM":
          return { ...state, showEditForm: true };
        case "CLOSE_EDIT_FORM":
          return { ...state, showEditForm: false };
        default:
          return state;
      }
    },
    {
      showEditForm: false,
    }
  );

  const onUpdateCallback = React.useCallback(
    async (values) => {
      await onUpdate(episode.id, values);
      console.log("....");
      dispatch({ type: "CLOSE_EDIT_FORM" });
    },
    [onUpdate, episode]
  );

  return (
    <li
      key={episode.id}
      className={c({ "border-t border-gray-200": bordered })}
    >
      <div className="px-4 py-4 flex items-center sm:px-6">
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <div className="text-sm leading-5 font-medium text-indigo-600 truncate">
              <span className={`inline-block mr-2 ${badge.indigo}`}>
                {episode.episode}
              </span>
              {episode.title}
              <span className="ml-1 font-normal text-gray-500">
                {`(${episode.video_length})`}
              </span>
            </div>
            <ul className="my-1">
              {episode.program_episode_part.map((part) => {
                const videoUrl = part.byteark
                  ? `http://true4u-kgjwb7.cdn.byteark.com/videos/${part.byteark}/embedded`
                  : part.youtube;
                return (
                  <li
                    key={part.id}
                    className="flex items-center text-sm leading-5 text-gray-500"
                  >
                    <div className="mr-1.5">
                      <VideoCamera></VideoCamera>
                    </div>
                    <a
                      href={videoUrl}
                      alt="youtube"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-500 hover:underline"
                    >
                      {videoUrl}
                    </a>
                  </li>
                );
              })}
            </ul>
            <div className="mt-2 flex">
              <div className="flex items-center text-sm leading-5 text-gray-500">
                <svg
                  className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>
                  Released on {formatDate(new Date(episode.released_date))}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="ml-5 flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              dispatch({ type: "OPEN_EDIT_FORM" });
            }}
            className="ml-1 inline-block bg-orange-500 text-white rounded-full p-2 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
          >
            <Pen size={4}></Pen>
          </button>
          <button
            type="button"
            onClick={() => {
              onDelete(episode.id);
            }}
            className="ml-1 inline-block bg-orange-500 text-white rounded-full p-2 hover:bg-orange-400 cursor-pointer focus:outline-none active:outline-none"
          >
            <Rubbish size={4}></Rubbish>
          </button>
        </div>
      </div>
      {state.showEditForm ? (
        <Form
          shadow={false}
          rounded={false}
          onSubmit={onUpdateCallback}
          submit="Save"
          onCancleClick={() => dispatch({ type: "CLOSE_EDIT_FORM" })}
          episode={{
            title: episode.title,
            episodeLength: episode.video_length,
            releasedDate: episode.released_date,
            videoLinks:
              episode.program_episode_part.length > 0
                ? episode.program_episode_part.map((part) => part.youtube)
                : [""],
            bytearkKeys:
              episode.program_episode_part.length > 0
                ? episode.program_episode_part.map((part) => part.byteark)
                : [""],
          }}
        ></Form>
      ) : null}
    </li>
  );
}

export function Form({
  onCancleClick,
  onSubmit,
  shadow = true,
  rounded = true,
  submit = "Create",
  episode,
}) {
  const releasedDateEl = React.useRef(null);
  const [releasedDate, setReleasedDate] = React.useState(null);
  const [formValues, setFormValues] = React.useState({
    title: "",
    episodeLength: "",
    releasedDate: "",
    videoLinks: [""],
    bytearkKeys: [""],
  });
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (episode) {
      setFormValues(episode);
      if (releasedDate) {
        releasedDate.setDate(new Date(episode.releasedDate));
      }
    }
  }, [episode, releasedDate]);

  React.useEffect(() => {
    const datePickerOptions = {
      enableTime: true,
      defaultHour: 0,
      time_24hr: true,
    };

    setReleasedDate(
      window.flatpickr(releasedDateEl.current, datePickerOptions)
    );
  }, []);

  const isValid = () => {
    return (
      formValues.title &&
      formValues.episodeLength &&
      (formValues.videoLinks[0] || formValues.bytearkKeys[0]) &&
      releasedDate.selectedDates[0]
    );
  };

  const handleFormValueChange = (name) => (event) => {
    setFormValues({
      ...formValues,
      [name]: event.target.value,
    });
  };

  const handleVideoLinkChange = React.useCallback(
    (index) => (event) => {
      formValues.videoLinks.splice(index, 1, event.target.value);
      setFormValues({ ...formValues });
    },
    [formValues]
  );

  const handleAddVideoLink = React.useCallback(() => {
    setFormValues({
      ...formValues,
      videoLinks: [...formValues.videoLinks, ""],
      bytearkKeys: [...formValues.bytearkKeys, ""],
    });
  }, [formValues]);

  const handleRemoveVideoLink = React.useCallback(
    (index) => () => {
      formValues.videoLinks.splice(index, 1);
      setFormValues({
        ...formValues,
      });
    },
    [formValues]
  );

  const handleBytearkKeyChange = React.useCallback(
    (key, index) => {
      formValues.bytearkKeys.splice(index, 1, key);
      setFormValues({ ...formValues });
    },
    [formValues]
  );

  const handleRemoveBytearkKey = React.useCallback(
    (index) => () => {
      formValues.bytearkKeys.splice(index, 1);
      setFormValues({
        ...formValues,
      });
    },
    [formValues]
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    const videoLinks = formValues.videoLinks.filter((l) => l);
    if (isValid()) {
      const values = {
        ...formValues,
        releasedDate: releasedDate.selectedDates
          ? releasedDate.selectedDates[0]?.toISOString()
          : null,
      };
      setError(null);
      onSubmit(values);
    } else {
      setError([
        formValues.title ? null : "Title is required.",
        formValues.episodeLength ? null : "Episode length is required.",
        videoLinks[0] || formValues.bytearkKeys[0]
          ? null
          : "Video link is required.",
        releasedDate.selectedDates[0] ? null : "Released date is required.",
      ]);
    }
  };

  const handleCancel = (event) => {
    event.preventDefault();
    onCancleClick();
  };

  return (
    <form className="mt-8" onSubmit={handleSubmit}>
      <div
        className={c("sm:overflow-hidden", {
          shadow: shadow,
          "sm:rounded-md": rounded,
        })}
      >
        <div className="px-4 py-5 bg-white sm:p-6">
          <p className="mt-2 text-sm text-gray-500">All fields are required</p>
          <div className="grid grid-cols-3 gap-6 mt-6">
            <div className="col-span-3 sm:col-span-2">
              <label
                htmlFor="title"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Title
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="title"
                  name="title"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.title}
                  onChange={handleFormValueChange("title")}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-4">
            <div className="col-span-3 sm:col-span-1">
              <label
                htmlFor="episodeLength"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Episode length
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="episodeLength"
                  name="episodeLength"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.episodeLength}
                  onChange={handleFormValueChange("episodeLength")}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-4">
            <div className="col-span-3 sm:col-span-1">
              <label
                htmlFor="releasedDate"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Released Date
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  ref={releasedDateEl}
                  id="releasedDate"
                  name="releasedDate"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>
          </div>
          {formValues.videoLinks.length <= 1 ? (
            <div className="grid grid-cols-3 gap-6 mt-4">
              <div className="col-span-3 sm:col-span-2">
                <label
                  htmlFor="videoLink"
                  className="block text-sm font-medium leading-5 text-gray-700"
                >
                  Video Link
                </label>
                <div className="flex mt-1 rounded-md shadow-sm items-center">
                  <label className="mr-2 text-sm font-medium text-gray-700">
                    Youtube
                  </label>
                  <input
                    name="videoLink"
                    className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                    value={formValues.videoLinks[0]}
                    onChange={handleVideoLinkChange(0)}
                  />
                </div>
                <div className="flex mt-2 rounded-md shadow-sm items-center">
                  <label className="mr-2 text-sm font-medium text-gray-700">
                    Byteark
                  </label>
                  <BrowseVideo
                    videoKey={formValues.bytearkKeys[0]}
                    index={0}
                    onUploadVideo={handleBytearkKeyChange}
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              <h5 className="-mx-6 border-b border-gray-200 font-medium mt-4 pb-2 px-6 text-gray-700 text-sm">
                Videos
              </h5>
              <div className="grid grid-cols-3 gap-6 mt-4">
                {formValues.videoLinks.map((link, index) => (
                  <div
                    key={index}
                    className="col-span-3 sm:col-span-2 flex flex-col"
                  >
                    <label
                      htmlFor="videoLink"
                      className="block text-sm font-medium leading-5 text-gray-700 mr-2"
                    >
                      Part {index + 1}
                    </label>
                    <div className="flex items-center rounded-md shadow-sm relative border-0">
                      <label className="mr-2 text-sm font-medium text-gray-700">
                        Youtube
                      </label>
                      <input
                        name="videoLink"
                        className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                        value={link}
                        onChange={handleVideoLinkChange(index)}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-1 flex items-center text-red-600 cursor-pointer hover:text-red-400 focus:text-red-400 focus:outline-none"
                        onClick={handleRemoveVideoLink(index)}
                      >
                        <XCircle size={6}></XCircle>
                      </button>
                    </div>
                    <div className="mt-2 pb-2 flex items-center rounded-md shadow-sm relative">
                      <label className="mr-2 text-sm font-medium text-gray-700">
                        Byteark
                      </label>
                      <BrowseVideo
                        videoKey={formValues.bytearkKeys[index]}
                        index={index}
                        onUploadVideo={handleBytearkKeyChange}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-3 bg-gray-50 sm:px-6">
          <div className="flex justify-end">
            <span className="inline-flex rounded-md shadow-sm">
              <button
                type="button"
                className={`py-2 px-4 rounded-md text-sm leading-5 font-medium ${button.plain}`}
                onClick={handleAddVideoLink}
              >
                Add Video
              </button>
            </span>
            <span className="flex-1"></span>
            <span className="inline-flex rounded-md shadow-sm">
              <button
                type="button"
                className={`py-2 px-4 rounded-md text-sm leading-5 font-medium ${button.plain}`}
                onClick={handleCancel}
              >
                Cancel
              </button>
            </span>
            <span className="ml-2 inline-flex rounded-md shadow-sm">
              <button
                type="submit"
                className={`inline-flex items-center px-4 py-2 text-sm leading-5 font-medium ${button.primary}`}
              >
                {submit}
              </button>
            </span>
          </div>
          {error ? <ErrorBlock error={error}></ErrorBlock> : null}
        </div>
      </div>
    </form>
  );
}

function ErrorBlock({ error }) {
  const items = error.filter((item) => item);
  return (
    <div className="rounded-md bg-red-50 p-4 mt-4 text-left">
      <div className="flex">
        <div className="flex-shrink-0">
          {/* Heroicon name: x-circle */}
          <svg
            className="h-5 w-5 text-red-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm leading-5 font-medium text-red-800">
            There were {items.length} error(s) with your submission
          </h3>
          <div className="mt-2 text-sm leading-5 text-red-700">
            <ul className="list-disc pl-5">
              {items.map((item, i) => {
                return (
                  <li key={i} className={c({ "mt-1": i > 0 })}>
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export function BrowseVideo({ videoKey = "", index = 0, onUploadVideo }) {
  const qoder = window.qoder;
  qoder.config.url =
    "https://qoder.byteark.com/apps/LnWakXMrQpfLkElk97Jz?access_token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.e30.ycuGXKSpHhxYiv1CBG-iyBib0U4dUo1smsqhBENhO4A";
  const handleClick = (e) => {
    try {
      qoder.browseVideo(function (video) {
        onUploadVideo(video.key, index);
      });
    } catch (error) {
      console.log(error);
    }
  };

  const url = videoKey
    ? `http://true4u-kgjwb7.cdn.byteark.com/videos/${videoKey}/embedded`
    : "";

  return (
    <div className="flex w-full">
      <button
        onClick={handleClick}
        type="button"
        className="mr-2 border text-sm px-3 py-1 rounded flex items-center"
      >
        Select Video
      </button>
      <input className="border flex-1 px-2 py-1 rounded" readOnly value={url} />
    </div>
  );
}
